import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getToken } from '../utils/getToken';

export interface DataInjection {
  id: string;
  url: string;
  method: 'GET' | 'POST';
  targetField: string;
  mapper: { label: string; value: string };
  body: Record<string, unknown> | null;
  cacheDuration: number;
}

export type InjectedDataMap = Map<string, Array<{ label: string; value: string | number }>>;

const injectionCache = new Map<string, { data: Array<{ label: string; value: string | number }>; timestamp: number }>();

export interface UseDataInjectionV2Result {
  injectedData: InjectedDataMap;
  subRowInjectedData: InjectedDataMap;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

function useStableInjections(injections: ReadonlyArray<DataInjection>): ReadonlyArray<DataInjection> {
  const ref = useRef(injections);
  const serialized = JSON.stringify(injections);
  const prevSerialized = useRef(serialized);
  if (prevSerialized.current !== serialized) {
    prevSerialized.current = serialized;
    ref.current = injections;
  }
  return ref.current;
}

export function useDataInjectionV2(
  injections: ReadonlyArray<DataInjection>,
  subRowInjections: ReadonlyArray<DataInjection> = [],
  baseUrl?: string,
  axiosInstance?: { get: Function; post: Function }
): UseDataInjectionV2Result {
  const stableInjections = useStableInjections(injections);
  const stableSubRowInjections = useStableInjections(subRowInjections);

  const [dataMap, setDataMap] = useState<InjectedDataMap>(new Map());
  const [subRowDataMap, setSubRowDataMap] = useState<InjectedDataMap>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const axiosInstanceRef = useRef(axiosInstance);
  axiosInstanceRef.current = axiosInstance;

  const fetchSingleInjection = useCallback(async (
    injection: DataInjection,
    controller: AbortController,
    ax: { get: Function; post: Function } | undefined,
    token: string | null,
    apiBase: string,
  ): Promise<{ field: string; data: Array<{ label: string; value: string | number }> }> => {
    const cacheKey = `${injection.url}_${JSON.stringify(injection.body)}`;
    const cached = injectionCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < injection.cacheDuration) {
      return { field: injection.targetField, data: cached.data };
    }

    let response: any;

    if (ax) {
      const path = injection.url.startsWith('http') ? injection.url : injection.url.replace(/^\//, '');
      if (injection.method === 'POST') {
        response = await ax.post(path, injection.body ?? {}, { signal: controller.signal });
      } else {
        response = await ax.get(path, { signal: controller.signal });
      }
    } else {
      const url = injection.url.startsWith('http')
        ? injection.url
        : `${apiBase}/${injection.url}`;

      response =
        injection.method === 'POST'
          ? await axios.post(url, injection.body ?? {}, {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
            })
          : await axios.get(url, {
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
            });
    }

    const rawData = Array.isArray(response.data) ? response.data : response.data?.items ?? [];
    const mappedData = rawData.map((item: Record<string, unknown>) => ({
      label: String(item[injection.mapper.label] ?? ''),
      value: item[injection.mapper.value] as string | number,
    }));

    injectionCache.set(cacheKey, { data: mappedData, timestamp: Date.now() });

    return { field: injection.targetField, data: mappedData };
  }, []);

  const fetchInjections = useCallback(async () => {
    const allInjections = [...stableInjections, ...stableSubRowInjections];
    if (allInjections.length === 0) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    try {
      const ax = axiosInstanceRef.current;
      const token = ax ? null : getToken();
      const apiBase = baseUrl ?? process.env.REACT_APP_WEALTHLANE_API_ENDPOINT ?? '';

      // Fetch main injections
      const mainResults = await Promise.allSettled(
        stableInjections.map((injection) => 
          fetchSingleInjection(injection, controller, ax, token, apiBase)
        )
      );

      // Fetch sub-row injections
      const subRowResults = await Promise.allSettled(
        stableSubRowInjections.map((injection) => 
          fetchSingleInjection(injection, controller, ax, token, apiBase)
        )
      );

      // Build main data map
      const newMainMap = new Map<string, Array<{ label: string; value: string | number }>>();
      mainResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          newMainMap.set(result.value.field, result.value.data);
        }
      });

      // Build sub-row data map
      const newSubRowMap = new Map<string, Array<{ label: string; value: string | number }>>();
      subRowResults.forEach((result) => {
        if (result.status === 'fulfilled') {
          newSubRowMap.set(result.value.field, result.value.data);
        }
      });

      setDataMap(newMainMap);
      setSubRowDataMap(newSubRowMap);
    } catch (err: unknown) {
      const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: unknown }).name) : '';
      if (name !== 'AbortError' && name !== 'CanceledError') {
        const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to fetch injection data';
        setError(message);
      }
    } finally {
      setIsLoading(false);
    }
  }, [stableInjections, stableSubRowInjections, baseUrl, fetchSingleInjection]);

  useEffect(() => {
    void fetchInjections();
    return () => abortRef.current?.abort();
  }, [fetchInjections]);

  return { 
    injectedData: dataMap, 
    subRowInjectedData: subRowDataMap, 
    isLoading, 
    error, 
    refetch: fetchInjections 
  };
}
